import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { handleError, userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import TabsList from "../../../components/Common/TabsList"
import { Box, Button, Container, Field, Flex, Input } from "@chakra-ui/react"
import useCustomToast from "../../../hooks/useCustomToast"
import { SubmitHandler, useForm } from "react-hook-form"
import { ApiError, SettingsSubscriptionsService, SettingsSubscriptionUpdate } from "../../../client"
import { readSubscriptionsSettings } from "../../../api/subscriptions"

export const Route = createFileRoute("/_layout/settings/subscriptions")({
  component: Subscriptions,
})

function SubscriptionsTable() {
    // Permission check
    const [sb_read, sb_update] = useMemo(() => {
        return ["read", "update"].map(
        (action) => userHasPermission([{ perm: `settings_subscriptions_${action}`, is_allowed: false }])[0].is_allowed
        )
    }, [userHasPermission])

    if(!sb_read) return <NoPermission/>

    // Query
    const queryClient = useQueryClient()
    const showToast = useCustomToast()

    const {data: subscriptionsSettings} = useQuery({
        ...readSubscriptionsSettings(),
    })

    //Form default values
    const defaultValues = {
        price_ch_year: subscriptionsSettings?.price_ch_year,
        price_ch_half_year: subscriptionsSettings?.price_ch_half_year,
        price_ch_quarter: subscriptionsSettings?.price_ch_quarter,
        price_europe: subscriptionsSettings?.price_europe,
        price_overseas: subscriptionsSettings?.price_overseas,
        vat: subscriptionsSettings?.vat,
    }

    // Form
    const {
        handleSubmit,
        reset,
        register,
        formState: {
        isSubmitting,
        errors,
        isDirty,
        },
    } = useForm<SettingsSubscriptionUpdate>({
        mode: "onBlur",
        criteriaMode: "all",
        defaultValues: defaultValues,
    })

    useEffect(() => {
        reset(defaultValues)
    }, [subscriptionsSettings])

    // Mutate form data
    const mutation = useMutation({
        mutationFn: (data: SettingsSubscriptionUpdate) =>
            SettingsSubscriptionsService.updateSettingsSubscription({ requestBody: data }),
        onSuccess: () => {
            showToast("Aktion erfolgreich!", "Abo Einstellungen wurden angepasst", "success")
        },
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["settings-subscriptions"] })
        },
    })

    // Submit form
    const onSubmit: SubmitHandler<SettingsSubscriptionUpdate> = async (data) => {
        if(data.price_ch_year == "") data.price_ch_year = 0
        if(data.price_ch_half_year == "") data.price_ch_half_year = 0
        if(data.price_ch_quarter == "") data.price_ch_quarter = 0
        if(data.price_europe == "") data.price_europe = 0
        if(data.price_overseas == "") data.price_overseas = 0
        if(data.vat == "") data.vat = 0

        mutation.mutate(data)
    }

    // Cancle form
    const onCancel = () => {
        reset(defaultValues)
    }

    return (
        <Container maxW="full">
            <Box
                w={{ sm: "full", md: "50%" }}
                as="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Field.Root mt={4} invalid={!!errors.price_ch_year}>
                    <Field.Label htmlFor="price_ch_year">Preis Schweiz Jahr</Field.Label>

                    <Input
                        id="price_ch_year"
                        {...register("price_ch_year")}
                        type="number"
                        placeholder="Preis Schweiz Jahr"
                        min={0}
                        step="0.01"
                    />

                    {errors.price_ch_year && (
                        <Field.ErrorText>{errors.price_ch_year.message}</Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root mt={4} invalid={!!errors.price_ch_half_year}>
                    <Field.Label htmlFor="price_ch_half_year">Preis Schweiz Halbjahr</Field.Label>

                    <Input
                        id="price_ch_half_year"
                        {...register("price_ch_half_year")}
                        type="number"
                        placeholder="Preis Schweiz Halbjahr"
                        min={0}
                        step="0.01"
                    />

                    {errors.price_ch_half_year && (
                        <Field.ErrorText>{errors.price_ch_half_year.message}</Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root mt={4} invalid={!!errors.price_ch_quarter}>
                    <Field.Label htmlFor="price_ch_quarter">Preis Schweiz Vierteljahr</Field.Label>

                    <Input
                        id="price_ch_quarter"
                        {...register("price_ch_quarter")}
                        type="number"
                        placeholder="Preis Schweiz Vierteljahr"
                        min={0}
                        step="0.01"
                    />

                    {errors.price_ch_quarter && (
                        <Field.ErrorText>{errors.price_ch_quarter.message}</Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root mt={4} invalid={!!errors.price_europe}>
                    <Field.Label htmlFor="price_europe">Preis Europa</Field.Label>

                    <Input
                        id="price_europe"
                        {...register("price_europe")}
                        type="number"
                        placeholder="Preis Europa"
                        min={0}
                        step="0.01"
                    />

                    {errors.price_europe && (
                        <Field.ErrorText>{errors.price_europe.message}</Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root mt={4} invalid={!!errors.price_overseas}>
                    <Field.Label htmlFor="price_overseas">Preis Übersee</Field.Label>

                    <Input
                        id="price_overseas"
                        {...register("price_overseas")}
                        type="number"
                        placeholder="Preis Übersee"
                        min={0}
                        step="0.01"
                    />

                    {errors.price_overseas && (
                        <Field.ErrorText>{errors.price_overseas.message}</Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root mt={4} invalid={!!errors.vat}>
                    <Field.Label htmlFor="vat">Mehrwertsteuer</Field.Label>

                    <Input
                        id="vat"
                        {...register("vat")}
                        type="number"
                        placeholder="Mehrwertsteuer"
                        min={0}
                        step="0.1"
                    />

                    {errors.vat && (
                        <Field.ErrorText>{errors.vat.message}</Field.ErrorText>
                    )}
                </Field.Root>

                <Flex mt={4} mb={8} gap={8}>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isDirty || !sb_update}
                    >
                        Speichern
                    </Button>

                    <Button onClick={onCancel} disabled={isSubmitting || !isDirty} variant="secondary">
                        Abbrechen
                    </Button>
                </Flex>
            </Box>
        </Container>
    )
}

export default SubscriptionsTable

function Subscriptions() {
  const tabs = useMemo(() => [
    { value: "settings", title: "Allgemein" },
    { value: "settings/navigation", title: "Navigation" },
    { value: "settings/landingpage", title: "Startseite" },
    { value: "settings/mimetypes", title: "Dateitypen" },
    { value: "settings/subscriptions", title: "Abos" },
    { value: "settings/priocategories", title: "Kategorien" },
    { value: "settings/ratecharts", title: "Tariftabellen" },
    { value: "settings/actions", title: "Aktionen" },
  ], [])

  return <TabsList tabs={tabs} defaultVal="settings" />
}
